import { z } from 'zod';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
class ApiError extends Error {
  constructor({
    code,
    apiMessage,
    userMessage,
    status,
    context
  }) {
    super(apiMessage);
    /** Appropriate HTTP Status */
    __publicField(this, "status");
    /** The Bedrock error code (or, UNKNOWN_ERROR) if something else unexpected happens */
    __publicField(this, "code");
    /** Message that the API returns, which is probably not appropriate for a user */
    __publicField(this, "api_message");
    /** Message that Bedrock regards as appropriate for a user */
    __publicField(this, "user_message");
    /**
     * Any additional information that may be helpful in diagnosing an error.
     *
     * This is on an "anything can happen" basis, so context could be anything at all!
     */
    __publicField(this, "context");
    this.status = status || 200;
    this.code = code;
    this.api_message = apiMessage;
    this.user_message = userMessage;
    this.context = context;
  }
}

async function request(options) {
  const { url } = options;
  const method = "body" in options ? options.method : "GET";
  const init = {
    method,
    headers: {
      "Content-Type": "application/json"
    }
  };
  if ("body" in options) {
    init.body = JSON.stringify(options.body);
  }
  const urlParams = new URLSearchParams(options.query).toString();
  const urlWithParams = urlParams ? `${url}?${urlParams}` : url;
  const response = await fetch(`/auth${urlWithParams}`, init);
  const json = await response.json();
  if (!json?.success || !("data" in json)) {
    throw handleError(json, response.status);
  }
  return options.responseSchema.parse(json?.data);
}
function handleError(json, status) {
  const maybeApiError = errorSchema.safeParse(json.error);
  if (maybeApiError.success) {
    const {
      code,
      api_message: apiMessage,
      user_message: userMessage
    } = maybeApiError.data;
    return new ApiError({
      code,
      apiMessage,
      userMessage,
      status
    });
  }
  return new ApiError({
    code: "UNKNOWN_ERROR",
    apiMessage: "An unknown error occurred.",
    userMessage: "An unexpected error occurred. Please try again later.",
    status: 500,
    context: json
  });
}
const errorSchema = z.object({
  code: z.string(),
  api_message: z.string(),
  user_message: z.string()
});

const preferenceValuesSchema = z.object({
  contentInterests: z.array(z.string()).catch([])
});
const getUserPreferencesResponseSchema = z.array(
  z.object({
    /** A object containing all stored preferences */
    values: preferenceValuesSchema,
    /** The category of preferences returned */
    category: z.enum(["settings", "preferences", "alerts"]),
    /** The platform these preferences belong to */
    platform: z.string()
  })
);
async function getUserPreferences(params) {
  const { user_id, ...body } = params;
  return await request({
    url: `/users/${user_id}/preferences`,
    body,
    responseSchema: getUserPreferencesResponseSchema
  });
}

const getSubscriptionsTermSchema = z.object({
  name: z.string().default(""),
  description: z.string().default(""),
  plan_periods: z.array(z.string()).catch([])
});
const getUserSubscriptionsResponseSchema = z.array(
  z.object({
    subscription_id: z.string().catch(""),
    product_name: z.string().catch(""),
    resource_id: z.string().catch(""),
    subscription_source: z.string().catch(""),
    subscription_status: z.string().catch(""),
    next_billing_date: z.string().datetime({ offset: true }).optional(),
    payment_method: z.string().catch(""),
    "billing-plan": z.string().catch(""),
    auto_renew: z.boolean().catch(false),
    term: getSubscriptionsTermSchema
  })
).catch([]);
async function getUserSubscriptions(params) {
  const { user_id, ...body } = params;
  return await request({
    url: `/users/${user_id}/subscriptions`,
    body,
    responseSchema: getUserSubscriptionsResponseSchema
  });
}

async function getUserProductAccess(options) {
  const { userId, serviceUrl, accessToken } = options;
  const response = await fetch(`${serviceUrl}/users/${userId}/product/access`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      "x-auth-token": accessToken
    }
  });
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error.api_message);
  }
  const { features, id, app_access: appAccess, email } = json.data;
  return {
    features,
    id,
    appAccess,
    email
  };
}

const signoutResponseSchema = z.unknown();
async function signout(params) {
  const { user_id, ...body } = params;
  return await request({
    url: `/users/${user_id}/signout`,
    method: "POST",
    body,
    // The response isn't used for anything, so we can just use z.unknown()
    responseSchema: signoutResponseSchema
  });
}

function getSessionToken(response) {
  const cookies = response.headers.get("set-cookie");
  const sessionMatch = cookies?.match(/SESSION=([^;]+)/);
  const sessionToken = sessionMatch ? sessionMatch[1] : void 0;
  return sessionToken;
}
async function getClientToken(options) {
  const { clientSecret, brand, serviceUrl } = options;
  const response = await fetch(`${serviceUrl}/auth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json"
    },
    body: JSON.stringify({
      scope: "read_write",
      brand,
      client_secret: clientSecret
    })
  });
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error.api_message);
  }
  const {
    client_token: clientToken,
    token_type: tokenType,
    expires_at: expiresAt,
    scope
  } = json.data;
  return {
    clientToken,
    tokenType,
    expiresAt,
    scope
  };
}
async function refreshToken(options) {
  const { serviceUrl, userId, sessionToken } = options;
  const response = await fetch(`${serviceUrl}/users/${userId}/auth/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      cookie: `SESSION=${sessionToken}`
    },
    body: JSON.stringify({
      scope: "users:read_write"
    })
  });
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error.api_message);
  }
  const {
    access_token: accessToken,
    token_type: tokenType,
    expires_at: expiresAt,
    scope
  } = json.data;
  return {
    accessToken,
    tokenType,
    expiresAt,
    scope
  };
}

async function updateUserPreferences(params) {
  const { user_id, ...body } = params;
  await request({
    url: `/users/${user_id}/preferences`,
    body,
    method: "PATCH",
    responseSchema: z.unknown()
  });
}

const registerUserSuccessSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  user_id: z.string(),
  access_token: z.string().nullable(),
  email_verified: z.boolean(),
  message: z.string().nullable(),
  platform: z.string(),
  external_id: z.string()
});

const verifyCodeSuccessSchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  user_id: z.string(),
  access_token: z.string().nullable(),
  email_verified: z.boolean(),
  message: z.string().nullable(),
  platform: z.string(),
  external_id: z.string()
});

async function registerUser(options) {
  const { brand, email, password, source } = options;
  return await request({
    url: "/users/register",
    body: { email, password, brand, source },
    responseSchema: registerUserSuccessSchema
  });
}
async function verifyCode(options) {
  return await request({
    url: "/users/otp/verify",
    body: options,
    responseSchema: verifyCodeSuccessSchema
  });
}
async function signInUser(options) {
  const { clientSecret, brand, serviceUrl, email, password, rememberMe } = options;
  const { clientToken } = await getClientToken({
    clientSecret,
    brand,
    serviceUrl
  });
  const response = await fetch(`${serviceUrl}/users/signin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      Authorization: `Bearer ${clientToken}`
    },
    body: JSON.stringify({
      email,
      password,
      brand,
      remember_me: rememberMe
    })
  });
  const sessionToken = getSessionToken(response);
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error.api_message);
  }
  const {
    first_name: firstName,
    last_name: lastName,
    user_id: userId,
    access_token: accessToken,
    email_verified: emailVerified,
    message,
    platform,
    external_id: externalId,
    app_access: appAccess,
    features
  } = json.data;
  return {
    firstName,
    lastName,
    userId,
    accessToken,
    emailVerified,
    message,
    platform,
    externalId,
    appAccess,
    features,
    sessionToken
  };
}
async function signOutUser(options) {
  const { userId, serviceUrl, sessionToken } = options;
  const response = await fetch(`${serviceUrl}/users/${userId}/signout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      accept: "application/json",
      cookie: `SESSION=${sessionToken}`
    },
    body: JSON.stringify(options)
  });
  const json = await response.json();
  if (!json.success) {
    throw new Error(json.error.api_message);
  }
  const { message } = json.data;
  return {
    message
  };
}

const testSubscriptionUsers = [
  {
    email: "ngdwebtestuser@gmail.com",
    userId: 21770748,
    subscriptions: {
      nbcNews: true,
      startToday: true
    }
  }
];

async function getAccessTokenCookie() {
  if (window.cookieStore) {
    const cookie = await window.cookieStore.get("access_token");
    return cookie?.value;
  }
  return Promise.resolve(
    document.cookie.split("; ").find((row) => row.startsWith("access_token="))?.split("=")[1]
  );
}
async function deleteAccessTokenCookie() {
  const cookies = [
    "access_token",
    "user_name",
    "SESSION",
    "bedrock_session",
    "tinypass_token"
  ];
  if (window.cookieStore) {
    await Promise.all(cookies.map((name) => window.cookieStore.delete(name)));
  } else {
    cookies.forEach((name) => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    });
  }
}
const accessTokenSchema = z.object({
  /** The expiration time of the token in seconds (since the epoch) */
  exp: z.number(),
  /** subject of the token, typically the user ID */
  sub: z.string().optional(),
  /** The user's email address */
  email: z.string().optional(),
  /** The user's name */
  given_name: z.string().optional(),
  /** The brand associated with the token */
  brand: z.string().optional(),
  /** Subscription entitlements */
  entitlements: z.object({
    StartToday: z.array(z.string()).optional(),
    SUBS_NBCNEWS: z.array(z.string()).optional()
  }).optional()
});
function parseAccessTokenJwt(token) {
  try {
    const parsed = JSON.parse(window.atob(String(token.split(".")[1])));
    return accessTokenSchema.parse(parsed);
  } catch {
    return void 0;
  }
}
async function tryGetAccessToken() {
  const accessToken = await getAccessTokenCookie();
  if (accessToken) {
    const parsed = parseAccessTokenJwt(accessToken);
    if (parsed) {
      const { exp } = parsed;
      const tokenExpiry = exp * 1e3;
      const isExpired = Date.now() >= tokenExpiry;
      if (!isExpired) {
        return parsed;
      }
    }
    await deleteAccessTokenCookie();
  }
  return void 0;
}
const listenerMap = /* @__PURE__ */ new WeakMap();
function addSignOutListener(handleSignOut) {
  if (window.cookieStore && !listenerMap.has(handleSignOut)) {
    const handler = (event) => {
      if (event.deleted.find((cookie) => cookie.name === "access_token")) {
        handleSignOut();
      }
    };
    listenerMap.set(handleSignOut, handler);
    cookieStore.addEventListener("change", handler);
  }
}
function removeSignOutListener(handleSignOut) {
  if (window.cookieStore) {
    const handler = listenerMap.get(handleSignOut);
    if (handler) {
      cookieStore.removeEventListener("change", handler);
      listenerMap.delete(handleSignOut);
    }
  }
}
function userSubscriptions(userData, testUsers = testSubscriptionUsers) {
  const { email, entitlements, sub: userId } = userData;
  const testUser = testUsers?.find(
    (user) => user?.email === email || user?.userId === userId
  );
  if (testUser) {
    return {
      subscriptions: {
        ...testUser.subscriptions
      }
    };
  }
  return {
    subscriptions: {
      nbcNews: !!entitlements?.SUBS_NBCNEWS?.includes("subs_nbcnews"),
      startToday: !!entitlements?.StartToday?.includes("start_today")
    }
  };
}

export { ApiError, addSignOutListener, getClientToken, getUserPreferences, getUserProductAccess, getUserSubscriptions, parseAccessTokenJwt, refreshToken, registerUser, removeSignOutListener, signInUser, signOutUser, signout, tryGetAccessToken, updateUserPreferences, userSubscriptions, verifyCode };
//# sourceMappingURL=index.mjs.map
